import { useState, useEffect, useMemo } from 'react';
import { formatDate, convertDateBRToISO } from '../utils/dateUtils';
import { getProfessionalName, formatPhoneNumber, getFormattedServicesList } from '../utils/entityUtils';
import { formatPhone } from '../utils/formatUtils';

/**
 * Hook para gerenciar a lógica de negócio do modal de agendamento
 * 
 * @param {Object} options - Opções de configuração
 * @param {Object} options.appointment - Objeto de agendamento
 * @param {boolean} options.isOpen - Estado de abertura do modal
 * @param {Function} options.onReschedule - Função para remarcar o agendamento
 * @param {Function} options.onCancel - Função para cancelar o agendamento
 * @param {Function} options.onEditServices - Função para editar serviços
 * @param {Function} options.onMarkAsPaid - Função para marcar como pago
 * @param {Function} options.onClose - Função para fechar o modal
 * @param {Array} options.services - Lista de serviços disponíveis
 * @param {Array} options.professionals - Lista de profissionais disponíveis
 * @returns {Object} - Estados e handlers para operações do modal
 */
export const useAppointmentModal = ({ 
  appointment, 
  isOpen, 
  onReschedule,
  onCancel,
  onEditServices,
  onMarkAsPaid,
  onClose,
  services = [],
  professionals = []
}) => {
  // Estado para armazenar os dados editados do agendamento
  const [appointmentData, setAppointmentData] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeScreen, setActiveScreen] = useState('details');
  
  // Estados para o fluxo de confirmação de pagamento
  const [showProfessionalConfirmation, setShowProfessionalConfirmation] = useState(false);
  const [showProfessionalSelection, setShowProfessionalSelection] = useState(false);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const [customPrice, setCustomPrice] = useState(null);
  
  // Novo estado para pergunta inicial de tipo de pagamento
  const [showPaymentTypeSelection, setShowPaymentTypeSelection] = useState(false);
  
  // Formatar dados do agendamento
  const formattedTime = appointment?.horario 
    ? appointment.horario.substring(0, 5) 
    : 'Não definido';
    
  // Status de pagamento
  const isPaid = appointment?.pago || appointment?.pagamentoConfirmado || false;
  const isPaidInAdvance = appointment?.pagamentoAntecipado || false;
  
  // Resetar o estado quando o modal abre ou quando muda o agendamento
  useEffect(() => {
    if (isOpen && appointment) {
      setActiveScreen('details');
      setErrorMessage('');
      setIsSubmitting(false);
      setShowProfessionalConfirmation(false);
      setShowProfessionalSelection(false);
      setSelectedProfessionalId(null);
      setCustomPrice(null);
      setShowPaymentTypeSelection(false);
      
      // Extrair o profissional ID do objeto appointment
      const profissionalId = typeof appointment.profissional === 'object' 
        ? appointment.profissional?.id 
        : appointment.profissional;
      
      // Preparar os dados do agendamento para edição - garantindo tipos consistentes
      let dataFormatada = '';
      let dataISO = '';
      
      // Processar a data corretamente, garantindo que temos os dois formatos
      if (appointment.data) {
        if (typeof appointment.data === 'string') {
          // Se a data é string, pode ser ISO ou BR
          if (appointment.data.includes('-')) {
            // É formato ISO
            dataISO = appointment.data;
            dataFormatada = formatDate(appointment.data);
          } else if (appointment.data.includes('/')) {
            // É formato BR
            dataFormatada = appointment.data;
            dataISO = convertDateBRToISO(appointment.data);
          } else {
            // Formato não reconhecido, tentar formatDate como fallback
            dataFormatada = formatDate(appointment.data);
            dataISO = appointment.data;
          }
        } else if (appointment.data instanceof Date) {
          // Se é objeto Date, formatar para os dois formatos
          dataFormatada = formatDate(appointment.data);
          const year = appointment.data.getFullYear();
          const month = String(appointment.data.getMonth() + 1).padStart(2, '0');
          const day = String(appointment.data.getDate()).padStart(2, '0');
          dataISO = `${year}-${month}-${day}`;
        }
      }
      
      // Preparar os dados para edição
      setAppointmentData({
        ...appointment,
        data: dataFormatada,
        dataISO: dataISO,
        profissional: profissionalId || ''
      });
      
      // Preparar os serviços selecionados
      const servicosIDs = Array.isArray(appointment.servicos) 
        ? appointment.servicos.map(s => typeof s === 'object' ? s.id : s)
        : [];
      setSelectedServices(servicosIDs);
    }
  }, [isOpen, appointment]);
  
  // Função para lidar com o fechamento do modal
  const handleClose = () => {
    // Se estiver em uma tela de edição, voltar para detalhes
    if (activeScreen !== 'details') {
      setActiveScreen('details');
    } else if (showProfessionalConfirmation || showProfessionalSelection) {
      setShowProfessionalConfirmation(false);
      setShowProfessionalSelection(false);
    } else {
      onClose();
    }
  };

  // Função para lidar com a ação de remarcação
  const handleRescheduleAction = () => {
    // Manter os valores já definidos no appointmentData
    const preparedData = {
      ...appointmentData,
      servicos: selectedServices,
      cliente: appointment.cliente || '',
      whatsapp: appointment.whatsapp || '',
      email: appointment.email || '',
      profissional: typeof appointment.profissional === 'object' 
        ? appointment.profissional?.id 
        : appointment.profissional || ''
    };
    
    setAppointmentData(preparedData);
    setActiveScreen('reschedule');
  };
  
  // Função para lidar com a ação de edição de serviços
  const handleEditServicesAction = () => {
    setActiveScreen('editServices');
  };
  
  // Função para lidar com a ação de cancelamento
  const handleCancelAction = () => {
    setActiveScreen('confirmCancel');
  };
  
  // Função para confirmar a ação de cancelamento
  const handleConfirmCancel = async () => {
    try {
      setIsSubmitting(true);
      await onCancel(appointment.id);
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      setErrorMessage('Erro ao cancelar agendamento. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para iniciar o fluxo de marcação de pagamento com pergunta sobre o tipo
  const handleMarkAsPaid = () => {
    // Mostrar a tela de seleção do tipo de pagamento
    setShowPaymentTypeSelection(true);
  };
  
  // Função para quando o serviço já foi realizado (continua o fluxo padrão)
  const handleServicePerformed = () => {
    setShowPaymentTypeSelection(false);
    setShowProfessionalConfirmation(true);
  };
  
  // Função para registrar um pagamento antecipado
  const handlePrePayment = async () => {
    try {
      setIsSubmitting(true);
      
      // Atualizar o agendamento marcando como pago antecipadamente, sem confirmar profissional
      await onMarkAsPaid(appointment.id, {
        pago: true,
        pagamentoAntecipado: true
      });
      
      setIsSubmitting(false);
      setShowPaymentTypeSelection(false);
      // Volta para a tela de detalhes
      setActiveScreen('details');
    } catch (error) {
      setErrorMessage('Erro ao registrar pagamento antecipado. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para confirmar que o profissional original realizou o atendimento
  const confirmOriginalProfessional = async () => {
    try {
      setIsSubmitting(true);
      
      // Obter o ID do profissional original
      const profissionalId = typeof appointment.profissional === 'object' 
        ? appointment.profissional?.id 
        : appointment.profissional;
      
      // Verificar se temos um ID de profissional válido
      if (!profissionalId) {
        throw new Error('Profissional não encontrado');
      }
      
      // Verificar se é um pagamento antecipado
      const updateData = {
        pago: true,
        profissionalConfirmado: true,
        profissionalConfirmadoId: profissionalId
      };
      
      // Se for um pagamento antecipado, manter esse status
      if (appointment.pagamentoAntecipado) {
        updateData.pagamentoAntecipado = true;
      } else {
        updateData.pagamentoAntecipado = false;
      }
      
      // Atualizar o agendamento
      await onMarkAsPaid(appointment.id, updateData);
      
      setIsSubmitting(false);
      setShowProfessionalConfirmation(false);
      // Volta para a tela de detalhes
      setActiveScreen('details');
    } catch (error) {
      setErrorMessage('Erro ao confirmar realização. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para mostrar a seleção de profissional diferente
  const showDifferentProfessionalSelection = () => {
    setShowProfessionalConfirmation(false);
    setShowProfessionalSelection(true);
  };
  
  // Função para selecionar um profissional diferente e marcar como pago
  const confirmDifferentProfessional = async () => {
    if (!selectedProfessionalId) {
      setErrorMessage('Por favor, selecione um profissional.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Log para verificar os dados antes do envio
      console.log('Enviando dados para confirmar realização:', {
        appointmentId: appointment.id,
        profissionalConfirmadoId: selectedProfessionalId,
        valorCustomizado: customPrice,
        pagamentoAntecipado: appointment.pagamentoAntecipado || false
      });
      
      // Verificar se é um pagamento antecipado
      const updateData = {
        pago: true,
        profissionalConfirmado: true,
        profissionalConfirmadoId: selectedProfessionalId
      };
      
      // Se houver um preço customizado, incluí-lo
      if (customPrice !== null) {
        updateData.valorCustomizado = customPrice;
      }
      
      // Se for um pagamento antecipado, manter esse status
      if (appointment.pagamentoAntecipado) {
        updateData.pagamentoAntecipado = true;
      } else {
        updateData.pagamentoAntecipado = false;
      }
      
      // Atualizar o agendamento
      await onMarkAsPaid(appointment.id, updateData);
      
      setIsSubmitting(false);
      setShowProfessionalSelection(false);
      setSelectedProfessionalId(null);
      setCustomPrice(null);
      // Volta para a tela de detalhes
      setActiveScreen('details');
    } catch (error) {
      setErrorMessage('Erro ao confirmar realização. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para salvar a remarcação
  const handleSaveReschedule = async (updatedData) => {
    try {
      setIsSubmitting(true);
      // Apenas enviar os dados que precisam ser alterados, preservando o restante
      await onReschedule(appointment.id, {
        data: updatedData.dataISO,
        horario: updatedData.horario,
        profissional: updatedData.profissional,
        // Manter os mesmos serviços e dados do cliente
        servicos: appointment.servicos,
        cliente: appointment.cliente,
        whatsapp: appointment.whatsapp,
        email: appointment.email
      });
      setIsSubmitting(false);
      setActiveScreen('details');
    } catch (error) {
      setErrorMessage('Erro ao remarcar agendamento. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para salvar a edição de serviços
  const handleSaveServices = async (updatedData) => {
    try {
      setIsSubmitting(true);
      
      // Extrair os serviços do objeto retornado do formulário
      const servicosIds = updatedData.servicos;
      
      // Calcular novo valor total baseado nos serviços selecionados
      const servicosDetalhes = services
        .filter(s => servicosIds.includes(s.id))
        .map(s => ({
          id: s.id,
          nome: s.nome,
          preco: s.preco || 0,
          duracao: s.duracao || 0
        }));
      
      const valorTotal = servicosDetalhes.reduce((sum, s) => sum + s.preco, 0);
      
      await onEditServices(appointment.id, {
        servicos: servicosIds,
        servicosDetalhes,
        valorTotal
      });
      
      setIsSubmitting(false);
      setActiveScreen('details');
    } catch (error) {
      setErrorMessage('Erro ao atualizar serviços. Tente novamente.');
      setIsSubmitting(false);
    }
  };
  
  // Função para selecionar/deselecionar serviços
  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };
  
  // Adicionando uma maneira segura de obter o nome do profissional
  const professionalName = useMemo(() => {
    if (!appointment) return '';
    
    // Se não tiver profissional, retornar texto padrão
    if (!appointment.profissional) return 'Profissional não definido';
    
    return getProfessionalName(appointment.profissional, professionals);
  }, [appointment, professionals]);

  // Adicionando uma maneira segura de obter a lista de serviços
  const servicesList = useMemo(() => {
    if (!appointment) return '';
    return getFormattedServicesList(appointment.servicos, services);
  }, [appointment, services]);
  
  // Função para iniciar o fluxo de confirmação de realização do serviço para pagamentos antecipados
  const handleConfirmServiceCompletion = () => {
    // Ir diretamente para a tela de confirmação do profissional
    setShowProfessionalConfirmation(true);
  };

  return {
    // Estados
    appointmentData,
    selectedServices,
    isSubmitting,
    errorMessage,
    activeScreen,
    showProfessionalConfirmation,
    showProfessionalSelection,
    showPaymentTypeSelection,
    selectedProfessionalId,
    customPrice,
    
    // Setters
    setActiveScreen,
    setIsSubmitting,
    setErrorMessage,
    setSelectedProfessionalId,
    setShowProfessionalConfirmation,
    setShowProfessionalSelection,
    setShowPaymentTypeSelection,
    setCustomPrice,
    
    // Utils de formatação
    formattedTime,
    formatPhone,
    isPaid,
    isPaidInAdvance,
    servicesList,
    professionalName,
    
    // Handlers de ação
    handleClose,
    handleRescheduleAction,
    handleEditServicesAction,
    handleCancelAction,
    handleConfirmCancel,
    handleMarkAsPaid,
    handleServicePerformed,
    handlePrePayment,
    handleConfirmServiceCompletion,
    confirmOriginalProfessional,
    showDifferentProfessionalSelection,
    confirmDifferentProfessional,
    handleSaveReschedule,
    handleSaveServices,
    handleServiceToggle,
    formatPhoneNumber: formatPhone
  };
};

export default useAppointmentModal; 